import { db, storage } from './firebase';
import { collection, doc, setDoc, getDocs, deleteDoc, getDoc, query, orderBy, limit } from 'firebase/firestore';
import { ref, uploadString, getDownloadURL, deleteObject } from 'firebase/storage';
import { MindMapNode, NodeLink } from '../types';

export type SnapshotReason = 'auto_guard' | 'manual' | 'before_restore' | 'before_pdf_upload' | 'before_import';

export type SnapshotResult =
  | { ok: true; snapshotId: string }
  | { ok: false; reason: 'too_large' | 'permission' | 'network' | 'unknown'; error?: unknown };

export interface SnapshotMeta {
  id: string;
  createdAt: number;
  reason: SnapshotReason;
  nodeCount: number;
  linkCount: number;
  imageRefCount: number;
  contentSize: number;
}

const MAX_AUTO_SNAPSHOTS = 20;
const MAX_MANUAL_SNAPSHOTS = 10;
// Hard limit for a single Storage upload — 50MB is generous, real data is text-only
const MAX_SNAPSHOT_BYTES = 50_000_000;

function stripBase64(node: MindMapNode): MindMapNode {
  const result = { ...node };
  // Strip raw base64 — keep Firebase Storage URLs (start with https://)
  if (result.imageBase64 && (result.imageBase64.startsWith('data:') || result.imageBase64.length > 10_000)) {
    result.imageBase64 = null;
  }
  if (result.drawingBase64 && (result.drawingBase64.startsWith('data:') || result.drawingBase64.length > 10_000)) {
    result.drawingBase64 = null;
  }
  if (result.attachments) {
    result.attachments = result.attachments.map(a => ({
      ...a,
      content: (a.content.startsWith('data:') || a.content.length > 10_000) ? '' : a.content,
    }));
  }
  return result;
}

export function isDestructiveChange(
  existing: MindMapNode[],
  incoming: MindMapNode[],
  existingLinks: NodeLink[],
  incomingLinks: NodeLink[]
): boolean {
  if (existing.length === 0) return false;

  const removedNodeCount = existing.length - incoming.length;

  const existingJson = JSON.stringify(existing.map(stripBase64));
  const incomingJson = JSON.stringify(incoming.map(stripBase64));
  const existingSize = new Blob([existingJson]).size;
  const incomingSize = new Blob([incomingJson]).size;
  const contentSizeDrop = existingSize > 0 ? (existingSize - incomingSize) / existingSize : 0;

  const existingImgCount = existing.filter(n => n.imageBase64?.startsWith('https://')).length;
  const incomingImgCount = incoming.filter(n => n.imageBase64?.startsWith('https://')).length;
  const imageRefDrop = existingImgCount > 0 ? (existingImgCount - incomingImgCount) / existingImgCount : 0;

  const linkDrop = existingLinks.length > 3
    ? (existingLinks.length - incomingLinks.length) / existingLinks.length
    : 0;

  return (
    (existing.length > 5 && incoming.length < existing.length * 0.7) ||
    removedNodeCount >= 5 ||
    contentSizeDrop > 0.4 ||
    imageRefDrop > 0.4 ||
    linkDrop > 0.4
  );
}

export async function saveSnapshot(
  userId: string,
  notebookId: string,
  nodes: MindMapNode[],
  links: NodeLink[],
  reason: SnapshotReason
): Promise<SnapshotResult> {
  try {
    const strippedNodes = nodes.map(stripBase64);
    const snapshotJson = JSON.stringify({ nodes: strippedNodes, links });
    const sizeBytes = new Blob([snapshotJson]).size;

    if (sizeBytes > MAX_SNAPSHOT_BYTES) {
      console.warn(`[Snapshot] Unreasonably large: ${sizeBytes} bytes`);
      return { ok: false, reason: 'too_large' };
    }

    const snapshotId = String(Date.now());
    const imageRefCount = nodes.filter(n => n.imageBase64?.startsWith('https://')).length;

    // Upload JSON payload to Firebase Storage (no size limit vs Firestore 1MB cap)
    const storagePath = `users/${userId}/snapshots/${notebookId}/${snapshotId}.json`;
    await uploadString(ref(storage, storagePath), snapshotJson, 'raw', { contentType: 'application/json' });

    // Store only metadata in Firestore — keep document tiny
    await setDoc(
      doc(db, 'users', userId, 'notebooks', notebookId, 'snapshots', snapshotId),
      {
        createdAt: Date.now(),
        reason,
        nodeCount: nodes.length,
        linkCount: links.length,
        imageRefCount,
        contentSize: sizeBytes,
        schemaVersion: 2,
        createdBy: userId,
        storagePath,
      }
    );

    // Prune is non-critical — fire and forget
    pruneOldSnapshots(userId, notebookId).catch(e => console.warn('[Snapshot] Prune failed:', e));

    return { ok: true, snapshotId };
  } catch (err: unknown) {
    console.error('[Snapshot] Save failed:', err);
    const msg = String(err instanceof Error ? err.message : err);
    if (msg.includes('permission') || msg.includes('PERMISSION_DENIED')) {
      return { ok: false, reason: 'permission', error: err };
    }
    if (msg.includes('network') || msg.includes('unavailable')) {
      return { ok: false, reason: 'network', error: err };
    }
    return { ok: false, reason: 'unknown', error: err };
  }
}

export async function listSnapshots(userId: string, notebookId: string): Promise<SnapshotMeta[]> {
  const col = collection(db, 'users', userId, 'notebooks', notebookId, 'snapshots');
  const q = query(col, orderBy('createdAt', 'desc'), limit(30));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({
    id: d.id,
    createdAt: d.data().createdAt as number,
    reason: d.data().reason as SnapshotReason,
    nodeCount: d.data().nodeCount as number,
    linkCount: d.data().linkCount as number,
    imageRefCount: d.data().imageRefCount as number,
    contentSize: d.data().contentSize as number,
  }));
}

export async function restoreSnapshot(
  userId: string,
  notebookId: string,
  snapshotId: string
): Promise<{ nodes: MindMapNode[]; links: NodeLink[] }> {
  const snap = await getDoc(doc(db, 'users', userId, 'notebooks', notebookId, 'snapshots', snapshotId));
  if (!snap.exists()) throw new Error('Snapshot not found');

  const d = snap.data();

  // schemaVersion 2+: data in Firebase Storage
  if (d.storagePath) {
    const url = await getDownloadURL(ref(storage, d.storagePath));
    const response = await fetch(url);
    const data = await response.json() as { nodes: MindMapNode[]; links?: NodeLink[] };
    return { nodes: data.nodes ?? [], links: data.links ?? [] };
  }

  // schemaVersion 1 legacy: data inline in Firestore document
  if (d.snapshotJson) {
    const data = JSON.parse(d.snapshotJson) as { nodes: MindMapNode[]; links?: NodeLink[] };
    return { nodes: data.nodes ?? [], links: data.links ?? [] };
  }

  throw new Error('Snapshot has no data payload');
}

async function pruneOldSnapshots(userId: string, notebookId: string): Promise<void> {
  const col = collection(db, 'users', userId, 'notebooks', notebookId, 'snapshots');
  const q = query(col, orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);

  const all = snap.docs.map(d => ({
    id: d.id,
    reason: d.data().reason as SnapshotReason,
    storagePath: d.data().storagePath as string | undefined,
  }));

  const manuals = all.filter(s => s.reason === 'manual');
  const autos = all.filter(s => s.reason !== 'manual');

  const toDelete = [
    ...autos.slice(MAX_AUTO_SNAPSHOTS),
    ...manuals.slice(MAX_MANUAL_SNAPSHOTS),
  ];

  await Promise.all(
    toDelete.map(async s => {
      // Delete Storage file first, then Firestore metadata
      if (s.storagePath) {
        await deleteObject(ref(storage, s.storagePath)).catch(() => {});
      }
      await deleteDoc(doc(db, 'users', userId, 'notebooks', notebookId, 'snapshots', s.id));
    })
  );
}
